import { FileDownloadOutlined, Refresh } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Fade,
  FormControl,
  Grow,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useEffect, useMemo, useState } from 'react'

import {
  ApplicationAnalyticsParams,
  ApplicationAnalyticsResponse,
  UserRole,
  WeeklyBreakdownRow,
  WeeklyMetricPoint,
  getApplicationAnalytics,
} from '../../api'
import { getRoleLabel } from '../../utils/permissions'
import styles from './ApplicationAnalytics.module.css'

dayjs.extend(isoWeek)

const ROLE_OPTIONS: UserRole[] = [UserRole.ADMIN, UserRole.DECODER, UserRole.OU_ORCHESTRATOR, UserRole.EDITOR, UserRole.VIEWER]

const DEFAULT_ROLES: UserRole[] = [UserRole.EDITOR]
const DEFAULT_RANGE_WEEKS = 6

const formatRoleLabelForExport = (role: string) => {
  const match = ROLE_OPTIONS.find(option => option === role)
  return match ? getRoleLabel(match) : role
}

const summarizeRoles = (roles?: string[]) => {
  if (!roles || roles.length === 0) {
    return 'All roles'
  }
  return roles.map(role => formatRoleLabelForExport(role)).join(', ')
}

const escapeCsvValue = (value: string) => {
  const stringValue = value ?? ''
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }
  return stringValue
}

const downloadCsvFile = (filename: string, rows: string[][]) => {
  if (typeof window === 'undefined') {
    return
  }
  const csvContent = rows.map(row => row.map(value => escapeCsvValue(value ?? '')).join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const buildBreakdownCsvSection = (
  title: string,
  weeks: WeeklyMetricPoint[],
  rows: WeeklyBreakdownRow[],
): string[][] => {
  const section: string[][] = [[title]]
  if (weeks.length === 0 || rows.length === 0) {
    section.push(['No data available'])
    return section
  }
  const headers = weeks.map(week => formatWeekLabel(week.year, week.week))
  section.push(['User', ...headers, 'Total'])
  const weeklyTotals = weeks.map(() => 0)
  let grandTotal = 0

  rows.forEach(row => {
    const countsByWeek = row.weekly_counts.reduce<Record<string, number>>((acc, point) => {
      acc[weekKey(point.year, point.week)] = point.count
      return acc
    }, {})
    const csvRow: string[] = [`${row.name ?? 'Unknown user'} (${row.email ?? 'No email on file'})`]
    weeks.forEach((week, index) => {
      const key = weekKey(week.year, week.week)
      const value = countsByWeek[key] ?? 0
      weeklyTotals[index] += value
      csvRow.push(value.toString())
    })
    csvRow.push(row.total.toString())
    grandTotal += row.total
    section.push(csvRow)
  })

  section.push(['Totals', ...weeklyTotals.map(total => total.toString()), grandTotal.toString()])

  return section
}

const formatDateRange = (start?: string, end?: string) => {
  if (!start || !end) {
    return ''
  }
  const startLabel = dayjs(start).format('MMM D, YYYY')
  const endLabel = dayjs(end).format('MMM D, YYYY')
  return `${startLabel} - ${endLabel}`
}

const formatWeekLabel = (year: number, week: number) => `W${week} (${year})`
const formatWeekChipLabel = (year: number, week: number) => `W${week.toString().padStart(2, '0')}`
const weekKey = (year: number, week: number) => `${year}-${week}`

const ApplicationAnalytics = () => {
  const [selectedRoles, setSelectedRoles] = useState<UserRole[]>(DEFAULT_ROLES)
  const [reloadKey, setReloadKey] = useState(0)
  const [metrics, setMetrics] = useState<ApplicationAnalyticsResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const defaultEndDate = dayjs()
  const defaultStartDate = defaultEndDate.subtract(DEFAULT_RANGE_WEEKS - 1, 'week').startOf('isoWeek')
  const [startDate, setStartDate] = useState<Dayjs>(defaultStartDate)
  const [endDate, setEndDate] = useState<Dayjs>(defaultEndDate)

  const startDateIso = useMemo(() => startDate?.startOf('day').toISOString(), [startDate])
  const endDateIso = useMemo(() => endDate?.endOf('day').toISOString(), [endDate])

  useEffect(() => {
    let isMounted = true
    const fetchMetrics = async () => {
      setIsLoading(true)
      setHasError(false)
      const params: ApplicationAnalyticsParams | undefined =
        selectedRoles.length > 0 || startDateIso || endDateIso
          ? {
              roles: selectedRoles.length > 0 ? selectedRoles : undefined,
              start_date: startDateIso,
              end_date: endDateIso,
            }
          : undefined
      const response = await getApplicationAnalytics(params)
      if (!isMounted) {
        return
      }
      if (!response) {
        setHasError(true)
        setMetrics(null)
      } else {
        setMetrics(response)
      }
      setIsLoading(false)
    }

    fetchMetrics()

    return () => {
      isMounted = false
    }
  }, [selectedRoles, reloadKey, startDateIso, endDateIso])

  const handleRolesChange = (event: SelectChangeEvent<typeof selectedRoles>) => {
    const value = event.target.value
    setSelectedRoles(typeof value === 'string' ? (value.split(',') as UserRole[]) : value)
  }

  const handleClearRoles = () => {
    setSelectedRoles(ROLE_OPTIONS)
  }

  const handleStartDateChange = (value: Dayjs | null) => {
    if (!value) return
    const normalized = value.startOf('day')
    if (endDate && normalized.isAfter(endDate)) {
      setEndDate(normalized)
    }
    setStartDate(normalized)
  }

  const handleEndDateChange = (value: Dayjs | null) => {
    if (!value) return
    const normalized = value.endOf('day')
    if (normalized.isBefore(startDate)) {
      setStartDate(normalized.subtract(DEFAULT_RANGE_WEEKS - 1, 'week'))
    }
    setEndDate(normalized)
  }

  const handleRefresh = () => {
    setReloadKey(prev => prev + 1)
  }

  const handleExportMetricsCsv = () => {
    if (!metrics) {
      return
    }
    const timestamp = dayjs().format('YYYYMMDD-HHmmss')
    const rows: string[][] = []
    rows.push(['Application Analytics - Metrics'])
    rows.push(['Range', displayRangeLabel])
    rows.push(['Roles', filtersRoleSummary])
    rows.push(['Weeks in view', metrics.filters.weeks.toString()])
    rows.push([''])
    rows.push(['Metric', 'Value', 'Details'])
    rows.push([
      'Unique Users (Active)',
      metrics.app_access.active_users_last_period.toString(),
      'Users who logged in at least once',
    ])
    rows.push(['Total Accesses', metrics.app_access.total_accesses.toString(), 'Sum of all logins over the period'])
    rows.push([
      'Adoption Percentage',
      `${metrics.app_access.adoption_percentage}%`,
      '(Active Users / Registered Users) × 100',
    ])
    rows.push(['Registered Users', metrics.app_access.registered_users_current.toString(), 'Total registered users'])
    rows.push([''])
    rows.push(['Weekly app access'])
    if (metrics.app_access.weekly_access.length === 0) {
      rows.push(['No weekly access data available'])
    } else {
      rows.push(['Week', 'Unique users'])
      metrics.app_access.weekly_access.forEach(point => {
        rows.push([formatWeekLabel(point.year, point.week), point.count.toString()])
      })
    }
    rows.push([''])
    rows.push(['Weekly chatbot usage'])
    if (metrics.app_usage.weekly_usage.length === 0) {
      rows.push(['No weekly usage data available'])
    } else {
      rows.push(['Week', 'Total messages'])
      metrics.app_usage.weekly_usage.forEach(point => {
        rows.push([formatWeekLabel(point.year, point.week), point.count.toString()])
      })
    }
    rows.push([''])
    buildBreakdownCsvSection(
      'App access breakdown',
      metrics.app_access.weekly_access,
      metrics.app_access.weekly_breakdown,
    ).forEach(row => rows.push(row))
    rows.push([''])
    buildBreakdownCsvSection(
      'Chatbot usage breakdown',
      metrics.app_usage.weekly_usage,
      metrics.app_usage.weekly_breakdown,
    ).forEach(row => rows.push(row))
    downloadCsvFile(`analytics-metrics-${timestamp}.csv`, rows)
  }

  const accessSeries = useMemo(() => metrics?.app_access.weekly_access ?? [], [metrics])
  const usageSeries = useMemo(() => metrics?.app_usage.weekly_usage ?? [], [metrics])
  const accessBreakdown = useMemo(() => metrics?.app_access.weekly_breakdown ?? [], [metrics])
  const usageBreakdown = useMemo(() => metrics?.app_usage.weekly_breakdown ?? [], [metrics])

  const rangeLabel = useMemo(() => formatDateRange(metrics?.filters.range_start, metrics?.filters.range_end), [metrics])
  const displayRangeLabel = rangeLabel || 'Not available'
  const filtersRoleSummary = useMemo(() => summarizeRoles(metrics?.filters?.roles), [metrics])

  const renderMetricCard = (label: string, value: number | string, helper?: React.ReactNode, testId?: string) => (
    <Grow
      in
      timeout={500}>
      <div
        className={styles.metricCard}
        data-testid={testId}>
        <Typography className={styles.metricLabel}>{label}</Typography>
        <Typography className={styles.metricValue}>{value}</Typography>
        {helper}
      </div>
    </Grow>
  )

  const renderBreakdownTable = (
    title: string,
    helper: string,
    weeks: WeeklyMetricPoint[],
    rows: WeeklyBreakdownRow[],
    emptyMessage: string,
  ) => {
    const headers = weeks.map(week => ({
      key: weekKey(week.year, week.week),
      label: formatWeekChipLabel(week.year, week.week),
    }))
    const columnSpan = headers.length + 2
    const tableRows = rows.map(row => ({
      data: row,
      counts: row.weekly_counts.reduce<Record<string, number>>((acc, point) => {
        acc[weekKey(point.year, point.week)] = point.count
        return acc
      }, {}),
    }))
    const weeklyTotals = headers.map(header => tableRows.reduce((sum, row) => sum + (row.counts[header.key] ?? 0), 0))
    const grandTotal = tableRows.reduce((sum, row) => sum + row.data.total, 0)

    return (
      <div className={styles.chartCard}>
        <Typography className={styles.chartTitle}>{title}</Typography>
        <Typography className={styles.helperText}>{helper}</Typography>
        <div className={styles.breakdownTableWrapper}>
          <table className={styles.breakdownTable}>
            <thead>
              <tr>
                <th className={styles.breakdownHeaderUser}>User</th>
                {headers.map(header => (
                  <th
                    key={header.key}
                    className={styles.breakdownWeekHeader}>
                    {header.label}
                  </th>
                ))}
                <th className={styles.breakdownTotalHeader}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    className={styles.breakdownEmptyCell}
                    colSpan={columnSpan}>
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                tableRows.map(row => (
                  <tr key={row.data.user_id}>
                    <td className={styles.breakdownUserCell}>
                      <div className={styles.breakdownUserContent}>
                        <span className={styles.breakdownUserName}>{row.data.name ?? 'Unknown user'}</span>
                        <span className={styles.breakdownUserEmail}>{row.data.email ?? 'No email on file'}</span>
                      </div>
                    </td>
                    {headers.map(header => (
                      <td
                        key={`${row.data.user_id}-${header.key}`}
                        className={styles.breakdownCountCell}>
                        {row.counts[header.key] ?? 0}
                      </td>
                    ))}
                    <td className={styles.breakdownTotalCell}>{row.data.total}</td>
                  </tr>
                ))
              )}
              <tr className={styles.breakdownTotalsRow}>
                <td className={styles.breakdownTotalsLabel}>Totals</td>
                {headers.map((header, index) => (
                  <td
                    key={`total-${header.key}`}
                    className={styles.breakdownTotalsCount}>
                    {weeklyTotals[index]}
                  </td>
                ))}
                <td className={styles.breakdownTotalsValue}>{grandTotal}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Typography className={styles.title}>Application Analytics</Typography>
          <Typography className={styles.subtitle}>
            Track platform access and chatbot engagement over the past few weeks.
          </Typography>
        </div>

        <div className={styles.filtersSection}>
          <div className={styles.filtersRow}>
            <FormControl
              className={styles.formControl}
              size="small">
              <InputLabel id="roles-filter-label">Filter by roles</InputLabel>
              <Select
                labelId="roles-filter-label"
                multiple
                value={selectedRoles}
                onChange={handleRolesChange}
                input={<OutlinedInput label="Filter by roles" />}
                renderValue={selected =>
                  selected.length === ROLE_OPTIONS.length || selected.length === 0 ? (
                    <Chip
                      size="small"
                      label="All roles"
                    />
                  ) : (
                    <Box className={styles.roleChips}>
                      {selected.map(role => (
                        <Chip
                          key={role}
                          size="small"
                          label={getRoleLabel(role)}
                        />
                      ))}
                    </Box>
                  )
                }>
                {ROLE_OPTIONS.map(role => (
                  <MenuItem
                    key={role}
                    value={role}
                    className={styles.menuItem}>
                    <Checkbox
                      className={styles.menuItemSelectedCheckbox}
                      size="small"
                      disableRipple
                      checked={selectedRoles.includes(role)}
                    />
                    {getRoleLabel(role)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className={styles.datePickers}>
                <DatePicker
                  label="Start date"
                  value={startDate}
                  maxDate={endDate}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="End date"
                  value={endDate}
                  minDate={startDate}
                  maxDate={dayjs()}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </div>
            </LocalizationProvider>

            <div className={styles.actions}>
              <IconButton
                className={styles.iconButton}
                aria-label="refresh analytics"
                size="small"
                onClick={handleRefresh}>
                <Refresh fontSize="small" />
              </IconButton>
              <Button
                className={styles.secondaryButton}
                disableElevation
                onClick={handleClearRoles}>
                Show all roles
              </Button>
            </div>
          </div>
          {hasError && (
            <Alert severity="warning">We could not load the latest analytics. Please refresh and try again.</Alert>
          )}
        </div>

        <div className={styles.tabContent}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <CircularProgress />
            </div>
          ) : !metrics ? (
            <div className={styles.emptyState}>
              <Typography variant="body1">No analytics data is available right now.</Typography>
            </div>
          ) : (
            <>
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Typography className={styles.rangeLabel}>{`Range: ${displayRangeLabel}`}</Typography>
                  <Button
                    className={styles.linkButton}
                    startIcon={<FileDownloadOutlined fontSize="small" />}
                    onClick={handleExportMetricsCsv}>
                    Export CSV
                  </Button>
                </div>
                <div className={styles.metricsGrid}>
                  {renderMetricCard(
                    'Unique Users',
                    metrics.app_access.active_users_last_period,
                    <Typography className={styles.helperText}>Users who logged in at least once</Typography>,
                    'unique-users-card',
                  )}
                  {renderMetricCard(
                    'Total Accesses',
                    metrics.app_access.total_accesses,
                    <Typography className={styles.helperText}>Sum of all logins (per day)</Typography>,
                    'total-accesses-card',
                  )}
                  {renderMetricCard(
                    'Adoption Percentage',
                    `${metrics.app_access.adoption_percentage}%`,
                    <Typography className={styles.helperText}>Active / Registered users</Typography>,
                    'adoption-percentage-card',
                  )}
                  {renderMetricCard(
                    'Registered Users',
                    metrics.app_access.registered_users_current,
                    <Typography className={styles.helperText}>Total users in the system</Typography>,
                    'registered-users-card',
                  )}
                </div>
              </div>

              <div className={styles.chartsGrid}>
                <Fade
                  in
                  timeout={600}>
                  <div className={styles.chartCard}>
                    <Typography className={styles.chartTitle}>Weekly app access</Typography>
                    <Typography className={styles.helperText}>
                      Unique users who accessed the platform each ISO week
                    </Typography>
                    {accessSeries.length === 0 ? (
                      <div className={styles.emptyState}>
                        <Typography variant="body2">No access trend data yet.</Typography>
                      </div>
                    ) : (
                      <LineChart
                        height={300}
                        xAxis={[
                          {
                            scaleType: 'point',
                            data: accessSeries.map(dataPoint => formatWeekLabel(dataPoint.year, dataPoint.week)),
                          },
                        ]}
                        series={[
                          {
                            data: accessSeries.map(dataPoint => dataPoint.count),
                            label: 'Unique users',
                            color: '#c62828',
                            showMark: true,
                          },
                        ]}
                      />
                    )}
                  </div>
                </Fade>

                <Fade
                  in
                  timeout={700}>
                  <div className={styles.chartCard}>
                    <Typography className={styles.chartTitle}>Weekly chatbot usage</Typography>
                    <Typography className={styles.helperText}>
                      Total user messages sent to the chatbot each ISO week
                    </Typography>
                    {usageSeries.length === 0 ? (
                      <div className={styles.emptyState}>
                        <Typography variant="body2">No usage trend data yet.</Typography>
                      </div>
                    ) : (
                      <BarChart
                        height={300}
                        xAxis={[
                          {
                            scaleType: 'band',
                            data: usageSeries.map(dataPoint => formatWeekLabel(dataPoint.year, dataPoint.week)),
                          },
                        ]}
                        series={[
                          {
                            data: usageSeries.map(dataPoint => dataPoint.count),
                            color: '#1e88e5',
                          },
                        ]}
                      />
                    )}
                  </div>
                </Fade>
              </div>

              <div className={styles.breakdownSection}>
                <Typography className={styles.sectionTitle}>Breakdown</Typography>
                <Typography className={styles.helperText}>
                  Review weekly activity per user for both access and chatbot usage.
                </Typography>
                <div className={styles.breakdownGrid}>
                  {renderBreakdownTable(
                    'App access breakdown',
                    'Unique accesses per user each week',
                    accessSeries,
                    accessBreakdown,
                    'No access breakdown data available yet.',
                  )}
                  {renderBreakdownTable(
                    'Chatbot usage breakdown',
                    'Messages sent per user each week',
                    usageSeries,
                    usageBreakdown,
                    'No usage breakdown data available yet.',
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ApplicationAnalytics
